import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import CloudUploadOutlined from '@mui/icons-material/CloudUploadOutlined';
import { Box, Paper, Skeleton, Stack, Typography } from '@mui/material';

import { uploadStatusLabel } from './helpers';

interface UploadingCardProps {
  filename: string;
  /**
   * Latest known artifact-level processing_status from SSE — drives the
   * caption copy. Defaults to `uploaded` immediately after the upload POST
   * resolves; transitions through `decomposed` / `parsing` / `parsed` as
   * stream events arrive (the parent rolls these into the local pending
   * state). When the first matching DocRef appears in the timeline, the
   * parent removes this card from the pending list and the real DocRef
   * card takes its place.
   */
  status?: string;
}

/**
 * Optimistic skeleton card shown immediately on file drop. Holds the
 * filename + a coarse status caption while the backend pipeline runs;
 * disappears when DocRefs appear in the timeline (matched by
 * `source_artifact_id`).
 */
export function UploadingCard({ filename, status = 'uploaded' }: UploadingCardProps) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        opacity: 0.85,
      }}
    >
      <Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
        <Box
          sx={{
            position: 'relative',
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden
        >
          <ArticleOutlined sx={{ fontSize: 18 }} />
          <Box
            component="span"
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -2,
              fontSize: 10,
              lineHeight: 1,
              color: 'primary.main',
              backgroundColor: 'background.paper',
              borderRadius: '50%',
              width: 14,
              height: 14,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloudUploadOutlined sx={{ fontSize: 10 }} />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, lineHeight: 1.3 }}
            noWrap
          >
            {filename}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}
          >
            {uploadStatusLabel(status)}
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ mt: 1, pl: 5.25 }}>
        <Stack spacing={0.5}>
          <Skeleton variant="text"  sx={{ width: "80%", height: 16 }}/>
          <Skeleton variant="text"  sx={{ width: "60%", height: 16 }}/>
        </Stack>
      </Box>
    </Paper>
  );
}
